import type { ReactElement } from 'react';

import type { ProfileInput } from '@/features/auth/api';

const MAX_BIO_LENGTH = 2000;

/** 画面で編集中のプロフィール（未入力は空文字で保持する） */
export interface ProfileDraft {
  display_name: string;
  company: string;
  position: string;
  phone: string;
  contact_email: string;
  bio: string;
}

/** APIから受け取った値を編集用の下書きにする */
export function toDraft(source: {
  display_name: string | null;
  company: string | null;
  position: string | null;
  phone: string | null;
  contact_email: string | null;
  bio: string | null;
}): ProfileDraft {
  return {
    display_name: source.display_name ?? '',
    company: source.company ?? '',
    position: source.position ?? '',
    phone: source.phone ?? '',
    contact_email: source.contact_email ?? '',
    bio: source.bio ?? '',
  };
}

/** 送信用（空文字はサーバー側で未設定として扱われる） */
export function toInput(draft: ProfileDraft): ProfileInput {
  return { ...draft };
}

/**
 * プロフィールの入力欄（本人＝アカウント設定、事務局＝担当者の代理編集で共通 / D-082）。
 * 項目は会員（マイプロフィール）と揃えてある。
 */
export function ProfileFields({
  draft,
  onChange,
  idPrefix,
}: {
  draft: ProfileDraft;
  onChange: (next: ProfileDraft) => void;
  /** 同じ画面に複数出しても id が衝突しないようにする */
  idPrefix: string;
}): ReactElement {
  const set = (key: keyof ProfileDraft) => (value: string) =>
    onChange({ ...draft, [key]: value });

  return (
    <>
      <div className="flex flex-wrap gap-4">
        <Field
          id={`${idPrefix}-display-name`}
          label="表示名"
          hint="受講生や他の担当者に表示されます"
          value={draft.display_name}
          maxLength={120}
          onChange={set('display_name')}
        />
        <Field
          id={`${idPrefix}-company`}
          label="会社名"
          value={draft.company}
          maxLength={255}
          onChange={set('company')}
        />
        <Field
          id={`${idPrefix}-position`}
          label="役職"
          value={draft.position}
          maxLength={255}
          onChange={set('position')}
        />
        <Field
          id={`${idPrefix}-phone`}
          label="電話番号"
          value={draft.phone}
          maxLength={50}
          onChange={set('phone')}
        />
        <Field
          id={`${idPrefix}-contact-email`}
          label="連絡先メールアドレス"
          hint="ログインに使うメールとは別に持てます"
          value={draft.contact_email}
          maxLength={255}
          type="email"
          onChange={set('contact_email')}
        />
      </div>

      <label htmlFor={`${idPrefix}-bio`} className="mb-1 mt-4 block text-wptext-muted">
        自己紹介（{MAX_BIO_LENGTH}文字まで）
      </label>
      <textarea
        id={`${idPrefix}-bio`}
        rows={5}
        maxLength={MAX_BIO_LENGTH}
        value={draft.bio}
        onChange={(event) => set('bio')(event.target.value)}
        className="mb-4 w-full max-w-2xl rounded-sm border border-wpborder px-3 py-2"
      />
    </>
  );
}

function Field({
  id,
  label,
  hint,
  value,
  maxLength,
  type = 'text',
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  maxLength: number;
  type?: string;
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-wptext-muted">{label}</span>
      <input
        id={id}
        type={type}
        maxLength={maxLength}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-64 rounded-sm border border-wpborder px-3 py-2"
      />
      {hint && <span className="mt-1 block text-xs text-wptext-muted">{hint}</span>}
    </label>
  );
}
